import { defineStore } from 'pinia';
import { usePlayerProfileStore } from '~/stores/player-profile';
import { useDailyCheckinStore } from '~/stores/daily-checkin';
import { clearDailyCheckinCache } from '~/handlers/fetch-daily-checkin.handler';
import { CrmLevelSyncError } from '~/adapters/api-sync-crm-level/apostatudo-api-post-sync-crm-level.adapter';
import { resolveAdapter } from '~/adapters/registry';
import { normalizeCrmLevelSlug } from '~/utils/apostatudo/crm-level-slug';
import { vipOrderFromTag } from '~/utils/apostatudo/player-vip';
import { levelTiers, tierIndexOfLevel } from '~/utils/levels';

// Regularização de divergência de nível CRM (Smartico × core).
//
// O core guarda um espelho da escada da Smartico e o expõe no /players/me
// (`crmLevel`); o widget da Smartico reporta o nível dele pela própria store
// (`currentUserLevelData`). Quando os dois discordam, o front pede ao core que
// se realinhe: POST /crm/users/{id}/sync-level, com o Bearer do JOGADOR.
//
// O que esta store garante, além da chamada em si, é o que NÃO acontece:
//
// - No máximo UMA chamada por detecção de divergência na sessão (`attempted*`):
//   quem observa são watchers, e watcher re-dispara — o freio mora aqui, não
//   em quem chama.
// - `unchanged` é sucesso: a fonte tem cache de 5 min por jogador, então logo
//   depois de uma mudança o core pode responder que nada mudou. Aceita-se e
//   pronto — rechamar "porque não funcionou" seria loop.
// - `vip_pinned: true` é divergência PERMANENTE por design (VIP pinado no
//   tier): marca resolvido e nunca mais chama por essa diferença.
// - Nenhuma recusa retenta: 404 (Smartico não conhece o jogador), 422 (gap de
//   configuração no core — só logar), 429 (rate limit da rota) e 503
//   (orçamento do warehouse esgotado — o backfill do core resolve depois).
// - Core sem registro (`crmLevel === null`) NÃO é divergência: chamar aí seria
//   a chamada preventiva a cada login que o contrato proíbe.
// - VIP não compara nada, SEJA DE ONDE VIER o sinal: `vip` do core no
//   /players/me, degrau atual da Smartico num elo VIP da régua (posição 30+,
//   o critério do resto do app) ou o `level` do core lendo como VIP. Decisão de produto: nível VIP
//   não se regulariza por aqui em nenhuma direção — pra ele a escada local é a
//   autoridade (e o pino torna a diferença permanente de qualquer jeito).
export const useCrmLevelSyncStore = defineStore('crm-level-sync', () => {
  const playerProfileStore = usePlayerProfileStore();
  const { crmLevel, vip, isProfileKnown } = storeToRefs(playerProfileStore);

  const { currentUserLevelData } = storeToRefs(useSmarticoUserInfos());

  // Nível do jogador lido pela POSIÇÃO na régua da Smartico — a mesma leitura
  // da página de níveis e do LevelUpWatcher.
  const { level: smarticoLevel } = useSmarticoLevel();

  // Resolvido uma vez no setup (precisa do contexto Nuxt pelo CDN das artes).
  const tiers = levelTiers();

  const { userId } = useUserData();

  // Assinatura "userId:coreSlug×smarticoSlug" da última divergência já tratada
  // (com sucesso OU recusa terminal). É por DETECÇÃO, não um booleano de
  // sessão: se o par de slugs mudar de novo (o jogador subiu de nível com a
  // aba aberta), é uma divergência nova e pode sincronizar de novo.
  const attemptedSignature = ref<string | null>(null);

  // Jogador confirmado como VIP pinado: divergência permanente, nunca mais
  // chamar nesta sessão, qualquer que seja o par de slugs.
  const pinnedUserId = ref<number | null>(null);

  const inFlight = ref(false);

  /** O que a Smartico reporta, na mesma língua do slug do core. */
  const smarticoSlug = computed(() =>
    normalizeCrmLevelSlug(currentUserLevelData.value?.name),
  );

  /**
   * A Smartico diz VIP: o degrau atual cai num elo `isVip` da tabela de elos
   * (posição 30+ na régua). É EXATAMENTE o critério do resto do app (página de
   * níveis, pódio, celebração de elo, LevelCard do Clube) — a guarda concorda
   * com o que o jogador vê na tela, e sobrevive a renomeação dos degraus.
   *
   * De propósito NÃO lê as `core_public_tags` nem parseia o nome do degrau: a
   * tag não reflete o VIP com confiança, e o nome é cosmético.
   */
  const isSmarticoVip = computed(
    () => !!tiers[tierIndexOfLevel(smarticoLevel.value)]?.isVip,
  );

  /**
   * O core diz VIP fora do campo `vip`: o próprio `level` guardado lê como VIP
   * ('VIP 4', 'SIGNATURE'). O payload contraditório `vip: null` + `level:
   * "VIP 4"` já aconteceu; lá não há posição de régua, então o texto é o que
   * resta.
   */
  const isCoreLevelVip = computed(() => {
    const text = crmLevel.value?.split(' - ')[0];

    return !!text && vipOrderFromTag(text) !== null;
  });

  const isVipAnywhere = computed(
    () => isSmarticoVip.value || isCoreLevelVip.value,
  );

  /** O que o core guarda, normalizado pelo mesmo funil. */
  const storedSlug = computed(() => normalizeCrmLevelSlug(crmLevel.value));

  /**
   * Só há divergência quando os DOIS lados são conhecidos e discordam. Lado
   * ausente é "ainda não sei", e "não sei" não vira chamada ao warehouse.
   * VIP em QUALQUER dos lados nunca diverge.
   */
  const isDiverged = computed(
    () =>
      !!userId.value &&
      isProfileKnown.value &&
      vip.value === null &&
      !isVipAnywhere.value &&
      !!storedSlug.value &&
      !!smarticoSlug.value &&
      storedSlug.value !== smarticoSlug.value,
  );

  /**
   * Dispara a regularização SE (e só se) há divergência ainda não tratada.
   * Idempotente por construção: pode ser chamada de watcher sem medo — quem
   * já foi tratado, está em vôo ou não diverge sai de graça.
   */
  async function syncIfDiverged() {
    const id = userId.value;

    if (!id || !isDiverged.value || inFlight.value) return;
    if (pinnedUserId.value === id) return;

    const signature = `${id}:${storedSlug.value}×${smarticoSlug.value}`;

    if (attemptedSignature.value === signature) return;

    // Marca ANTES de chamar: a detecção está tratada mesmo se a resposta
    // falhar — nenhum caminho de erro (exceto 401, abaixo) admite retentar.
    const previousSignature = attemptedSignature.value;

    attemptedSignature.value = signature;
    inFlight.value = true;

    try {
      const result = await resolveAdapter('apiSyncCrmLevel').handle({
        userId: id,
      });

      // A conta pode ter trocado enquanto a resposta vinha.
      if (userId.value !== id) return;

      if (result.vipPinned) {
        pinnedUserId.value = id;
      }

      // `level` é o slug final no core — inclusive no `unchanged`, em que ele
      // apenas confirma o que já estava lá (cache de 5 min da fonte).
      playerProfileStore.acceptSyncedLevel(id, result.level);

      // `acceptSyncedLevel` grava `result.level` no `crmLevel`, que é reativo:
      // sem isto, o novo `storedSlug` geraria um par novo e o watcher
      // dispararia UMA SEGUNDA chamada. Assinar o par resultante fecha o ciclo
      // — o contrato é "uma chamada por detecção", não uma por escrita.
      const acceptedSlug = normalizeCrmLevelSlug(result.level);

      if (acceptedSlug) {
        attemptedSignature.value = `${id}:${acceptedSlug}×${smarticoSlug.value}`;
      }

      // O core mudou o nível do jogador → o que ele deriva desse nível ficou
      // velho. Hoje isso é o board do Check-in Diário (a segmentação por nível
      // CRM escolhe trilha, prêmios e termos pelo `crm_users.level_id`), que o
      // handler guarda por 5 min: sem isto, o jogador continuaria vendo a
      // trilha do nível antigo até o cache vencer. Descarta o cache e, se o
      // modal estiver aberto, repõe a trilha por baixo (`silent`: sem piscar
      // skeleton, e uma falha aqui preserva o que está na tela).
      //
      // Só em `created`/`updated`: `unchanged` não mudou nada no core, e
      // invalidar aí seria um GET a mais por nada. Enquanto o core não segmenta
      // o check-in por nível, a releitura devolve o mesmo board — custa uma
      // request rara (sync é evento de divergência) e não muda a tela.
      if (result.status !== 'unchanged') {
        clearDailyCheckinCache();

        const dailyCheckin = useDailyCheckinStore();

        if (dailyCheckin.isOpen) dailyCheckin.refresh({ silent: true });
      }
    } catch (err) {
      if (err instanceof CrmLevelSyncError) {
        // 401 não é recusa de negócio: é token do jogador expirado/ainda não
        // pronto (estado transitório). Diferente de 404/422/429/503, ele pode
        // resolver sozinho quando o header voltar válido — então liberamos a
        // assinatura pra retentar esse par, em vez de queimá-lo na sessão.
        if (err.kind === 'unauthenticated') {
          attemptedSignature.value = previousSignature;

          return;
        }

        // 503 é desistência silenciosa por contrato (o backfill do core
        // resolve); o resto é logado pra triagem — gap de configuração (422)
        // é bug de setup, não do jogador.
        if (err.kind !== 'budget_exhausted') {
          console.error(
            `[crm-level-sync] sync recusado (${err.kind}):`,
            err.message,
          );
        }
      } else {
        console.error('[crm-level-sync] falha inesperada:', err);
      }
    } finally {
      inFlight.value = false;
    }
  }

  /** Troca de conta / logout: a divergência tratada era daquele jogador. */
  function reset() {
    attemptedSignature.value = null;
    pinnedUserId.value = null;
    inFlight.value = false;
  }

  return {
    isDiverged,
    syncIfDiverged,
    reset,
  };
});
